import {
  CloseCircleOutlined,
  ReloadOutlined,
  CloudDownloadOutlined,
  UploadOutlined,
  DeleteFilled,
  PlayCircleOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { AccelFile } from "@axl/membership-sdk";
import { Popconfirm, Tooltip, Button, Space, Popover } from "antd";
import { SizeType } from "antd/es/config-provider/SizeContext";

import { useLoc } from "@app/shared/lib/hooks";

import { FileUploadStatus } from "../../FileListItem/FileListItem";
import { AudioPlayer } from "@app/shared/ui/AudioPlayer/AudioPlayer";

type FileActionsProps = {
  file: AccelFile;
  downloadable: boolean;
  deletable: boolean;
  replaceable: boolean;
  uploadStatus: FileUploadStatus;
  theme: "light" | "dark";
  size: SizeType;
  onReplace: () => void;
  onDownload: () => void;
  onUpload: () => void;
  onPreview: () => void;
  onRemove: () => void;
  onAbort: () => void;
};

export const FileActions: React.FC<FileActionsProps> = ({
  file,
  downloadable,
  replaceable,
  deletable,
  uploadStatus,
  theme,
  size,
  onReplace,
  onDownload,
  onUpload,
  onRemove,
  onAbort,
  onPreview
}) => {
  const loc = useLoc();

  return (
    <Space size={size === "small" ? 2 : 5}>
      {uploadStatus === FileUploadStatus.Uploading && (
        <Button
          size={size}
          type="text"
          icon={<CloseCircleOutlined />}
          title={loc.word("Cancel")}
          onClick={() => onAbort()}
          className="p-0"
        />
      )}

      {replaceable && uploadStatus === FileUploadStatus.Uploaded && (
        <Button
          size={size}
          type="text"
          icon={
            <UploadOutlined
              className={theme === "light" ? "color-white" : undefined}
            />
          }
          title={loc.word("File.replaceMsg", { default: "Replace file" })}
          onClick={() => onReplace()}
          className="p-0"
        />
      )}

      {(file.isAudio && uploadStatus === FileUploadStatus.Uploaded) && (
        <Popover
          destroyOnHidden
          trigger={'click'}
          styles={{ content: { width: '300px' } }}
          content={
            <AudioPlayer
              file={file}
              size={size}
              downloadable={downloadable}
            />
          }
        >
          <Button type='link' icon={<PlayCircleOutlined />} />
        </Popover>
      )}

      {(uploadStatus == FileUploadStatus.Uploaded && !file.isAudio && downloadable) &&
        <Button size={size}
          type='text'
          icon={<EyeOutlined className={theme == 'light' ? 'color-white' : undefined} />}
          title={loc.word('Global.preview')}
          onClick={onPreview}
          className='p-0'
        />
      }

      {downloadable && uploadStatus === FileUploadStatus.Uploaded && (
        <Button
          size={size}
          type="text"
          icon={
            <CloudDownloadOutlined
              className={theme === "light" ? "color-white" : undefined}
            />
          }
          title={loc.word("Download")}
          onClick={() => onDownload()}
          className="p-0"
        />
      )}

      {uploadStatus === FileUploadStatus.Error && (
        <Tooltip
          title={loc.word("File.uploadErrorMsg", {
            default: "Click to retry upload",
          })}
        >
          <Button
            size={size}
            type="text"
            danger
            icon={
              <ReloadOutlined
                className={theme === "light" ? "color-white" : undefined}
              />
            }
            onClick={() => onUpload()}
            className="p-0"
          />
        </Tooltip>
      )}

      {deletable === true && uploadStatus !== FileUploadStatus.Uploading && (
        <Popconfirm
          title={loc.word("File.deleteConfirmation", {
            default: "Are you sure you want to delete this file?",
          })}
          onConfirm={() => onRemove()}
        >
          <Button
            size={size}
            type="text"
            danger
            icon={<DeleteFilled />}
            className="p-0"
          />
        </Popconfirm>
      )}
    </Space>
  );
};
